import {isNotEmptyAndDefined} from '@/helpers/shadcn/utils';
import {flattenSegments} from '@/utils/commons';
import {
  isVideoActivityType,
  isFreeKickActivityType,
  isBackgammonActivityType,
  ACTIVITY_TYPE,
  ACTIVITY_TYPES_ENUM
} from '@/types/Activity';
import {isSurveyDisplayOrderInvalid} from '@/utils/displayOrderValidation';

export const validateDate = (dateValid) => dateValid === true;

export const checkDateValid = (date, validateFn) => {
  if (!date) return false;
  const d = validateFn(date);
  return d === undefined || d === null;
};

const getIsEditMode = (formData) =>
  formData.activityName || formData.uuid || formData.status;

const validateDailyPlayLimit = (dailyPlayLimit) => {
  if (
    !isNotEmptyAndDefined(dailyPlayLimit) ||
    dailyPlayLimit === 0 ||
    dailyPlayLimit === '0'
  ) {
    return true;
  }

  const dailyPlayLimitStr = String(dailyPlayLimit).trim();
  if (dailyPlayLimitStr === '' || /[^0-9]/.test(dailyPlayLimitStr)) {
    return true;
  }

  const dailyPlayLimitNum = Number(dailyPlayLimit);
  return (
    isNaN(dailyPlayLimitNum) ||
    !Number.isInteger(dailyPlayLimitNum) ||
    dailyPlayLimitNum < 1 ||
    dailyPlayLimitNum > 99
  );
};

const getNoSegmentsSelected = (formData) => {
  const selectedSegs = formData?.selectedSegments ?? formData?.segments;
  const hasNoIncludedSegments =
    formData?.allCustomers === false &&
    (!Array.isArray(selectedSegs) ||
      flattenSegments(selectedSegs).length === 0);

  // If segmentExclude is true, excludedSegments must have at least one item
  const hasNoExcludedSegments =
    formData?.segmentExclude === true &&
    (!Array.isArray(formData?.excludedSegments) ||
      formData.excludedSegments.length === 0);

  return hasNoIncludedSegments || hasNoExcludedSegments;
};

const getLoginInvalidChecks = ({
  formData,
  dateFns,
  validateStartDate = true
}) => {
  const {
    startDate,
    endDate,
    pointsEarned,
    receivePoints,
    dailyLoginLimit,
    pointsEarnedValid,
    dailyLoginLimitValid,
    allTimeLoginLimitValid
  } = formData;

  const isStartDateInvalid = !isNotEmptyAndDefined(startDate);
  const validStartDate = validateStartDate
    ? checkDateValid(startDate, dateFns.validateStartDate)
    : true;
  const isEndDateInvalid = !isNotEmptyAndDefined(endDate);
  const isPointsEarnedInvalid = !isNotEmptyAndDefined(pointsEarned);
  const isReceivePointsInvalid = !(
    typeof receivePoints === 'string' && isNotEmptyAndDefined(receivePoints)
  );
  const isDailyLoginLimitInvalid = !isNotEmptyAndDefined(dailyLoginLimit);

  const hasPointsEarnedError = pointsEarnedValid === true;
  const hasDailyLoginLimitError = dailyLoginLimitValid === true;
  const hasAllTimeLoginLimitError = allTimeLoginLimitValid === true;
  const validEndDate = checkDateValid(endDate, dateFns.validateEndDate);

  return {
    isStartDateInvalid,
    validStartDate,
    isEndDateInvalid,
    isPointsEarnedInvalid,
    isReceivePointsInvalid,
    isDailyLoginLimitInvalid,
    hasPointsEarnedError,
    hasDailyLoginLimitError,
    hasAllTimeLoginLimitError,
    validEndDate
  };
};

// ─── Login ───

export const isInvalidLoginDataForEdit = (params) => {
  const checks = getLoginInvalidChecks(params);
  return (
    !checks.validStartDate ||
    checks.hasPointsEarnedError ||
    checks.hasDailyLoginLimitError ||
    checks.hasAllTimeLoginLimitError ||
    checks.isStartDateInvalid ||
    checks.isEndDateInvalid ||
    checks.isReceivePointsInvalid ||
    checks.isPointsEarnedInvalid ||
    !checks.validEndDate ||
    checks.isDailyLoginLimitInvalid
  );
};

export const isInvalidLoginDataForCreation = (params) => {
  const checks = getLoginInvalidChecks(params);
  return (
    checks.hasPointsEarnedError ||
    !checks.validStartDate ||
    checks.hasDailyLoginLimitError ||
    checks.hasAllTimeLoginLimitError ||
    checks.isStartDateInvalid ||
    checks.isEndDateInvalid ||
    checks.isReceivePointsInvalid ||
    checks.isPointsEarnedInvalid ||
    !checks.validEndDate ||
    checks.isDailyLoginLimitInvalid
  );
};

export const isInvalidLoginData = (params) => {
  if (getIsEditMode(params.formData)) {
    return isInvalidLoginDataForEdit(params);
  }
  return isInvalidLoginDataForCreation(params);
};

const getWelcomeCommonChecks = (formData) => {
  const {startDate, endDate, pointsEarned, pointsEarnedValid, hasEndDate} =
    formData;

  const isPointsEarnedInvalid = !isNotEmptyAndDefined(pointsEarned);
  const isStartDateMissing = !isNotEmptyAndDefined(startDate);
  const hasPointsError = pointsEarnedValid === true || isPointsEarnedInvalid;

  if (hasEndDate) {
    const isEndDateMissing = !isNotEmptyAndDefined(endDate);
    return {hasPointsError, isStartDateMissing, isEndDateMissing, hasEndDate};
  }
  return {hasPointsError, isStartDateMissing, hasEndDate};
};

// ─── Welcome ───

export const isInvalidWelcomeDataForEdit = ({
  formData,
  dateFns,
  validateStartDate = true
}) => {
  const common = getWelcomeCommonChecks(formData);
  const validStartDate = validateStartDate
    ? checkDateValid(formData.startDate, dateFns.validateStartDate)
    : true;
  const hasStartDateError = formData.startDateValid === true;
  const hasEndDateError = formData.endDateValid === true;

  if (common.hasEndDate) {
    return (
      !validStartDate ||
      common.hasPointsError ||
      hasStartDateError ||
      hasEndDateError ||
      common.isStartDateMissing ||
      common.isEndDateMissing
    );
  }

  return (
    !validStartDate ||
    common.hasPointsError ||
    hasStartDateError ||
    common.isStartDateMissing
  );
};

export const isInvalidWelcomeDataForCreation = ({
  formData,
  validateStartDate = true
}) => {
  const common = getWelcomeCommonChecks(formData);
  const isStartDateInvalid = validateStartDate
    ? validateDate(formData.startDateValid)
    : true;
  const isEndDateInvalid = validateDate(formData.endDateValid);

  if (common.hasEndDate) {
    return (
      common.hasPointsError ||
      isStartDateInvalid ||
      isEndDateInvalid ||
      common.isStartDateMissing ||
      common.isEndDateMissing
    );
  }

  return (
    common.hasPointsError || isStartDateInvalid || common.isStartDateMissing
  );
};

export const isInvalidWelcomeData = (params) => {
  if (getIsEditMode(params.formData)) {
    return isInvalidWelcomeDataForEdit(params);
  }
  return isInvalidWelcomeDataForCreation(params);
};

// ─── Easy Order ───

const isNonNegativeNumericFieldInvalid = (value, valueValid) => {
  if (!isNotEmptyAndDefined(value)) {
    return true;
  }
  const trimmed = String(value).trim();
  if (!/^\d+$/.test(trimmed)) {
    return true;
  }
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) {
    return true;
  }
  return valueValid === true;
};

const isPhotoOrderModalDisplayLimitInvalid = (value) => {
  if (value === '' || value === undefined || value === null) {
    return true;
  }
  const n = Number(String(value).trim());
  return !Number.isFinite(n) || n <= 0 || !Number.isInteger(n);
};

export const isEasyOrderPhotoOrderModalDisplayLimitInvalid =
  isPhotoOrderModalDisplayLimitInvalid;

const getEasyOrderCommonChecks = (formData) => {
  const {
    startDate,
    endDate,
    pointsForSuccessfulUpload,
    pointsPerLineItem,
    photoOrderModalDisplayLimit,
    pointsForSuccessfulUploadValid,
    pointsPerLineItemValid,
    hasEndDate
  } = formData;

  const isStartDateMissing = !isNotEmptyAndDefined(startDate);
  const hasPointsForUploadError = isNonNegativeNumericFieldInvalid(
    pointsForSuccessfulUpload,
    pointsForSuccessfulUploadValid
  );
  const hasPointsPerLineItemError = isNonNegativeNumericFieldInvalid(
    pointsPerLineItem,
    pointsPerLineItemValid
  );
  const hasPhotoOrderLimitError = isPhotoOrderModalDisplayLimitInvalid(
    photoOrderModalDisplayLimit
  );

  if (hasEndDate) {
    const isEndDateMissing = !isNotEmptyAndDefined(endDate);
    return {
      hasPointsForUploadError,
      hasPointsPerLineItemError,
      hasPhotoOrderLimitError,
      isStartDateMissing,
      isEndDateMissing,
      hasEndDate
    };
  }

  return {
    hasPointsForUploadError,
    hasPointsPerLineItemError,
    hasPhotoOrderLimitError,
    isStartDateMissing,
    hasEndDate
  };
};

export const isInvalidEasyOrderDataForEdit = ({
  formData,
  dateFns,
  validateStartDate = true
}) => {
  const common = getEasyOrderCommonChecks(formData);
  const validStartDate = validateStartDate
    ? checkDateValid(formData.startDate, dateFns.validateStartDate)
    : true;
  const hasStartDateError = formData.startDateValid === true;
  const hasEndDateError = formData.endDateValid === true;

  if (common.hasEndDate) {
    return (
      !validStartDate ||
      common.hasPointsForUploadError ||
      common.hasPointsPerLineItemError ||
      common.hasPhotoOrderLimitError ||
      hasStartDateError ||
      hasEndDateError ||
      common.isStartDateMissing ||
      common.isEndDateMissing
    );
  }

  return (
    !validStartDate ||
    common.hasPointsForUploadError ||
    common.hasPointsPerLineItemError ||
    common.hasPhotoOrderLimitError ||
    hasStartDateError ||
    common.isStartDateMissing
  );
};

export const isInvalidEasyOrderDataForCreation = ({
  formData,
  validateStartDate = true
}) => {
  const common = getEasyOrderCommonChecks(formData);
  const isStartDateInvalid = validateStartDate
    ? validateDate(formData.startDateValid)
    : true;
  const isEndDateInvalid = validateDate(formData.endDateValid);

  if (common.hasEndDate) {
    return (
      common.hasPointsForUploadError ||
      common.hasPointsPerLineItemError ||
      common.hasPhotoOrderLimitError ||
      isStartDateInvalid ||
      isEndDateInvalid ||
      common.isStartDateMissing ||
      common.isEndDateMissing
    );
  }

  return (
    common.hasPointsForUploadError ||
    common.hasPointsPerLineItemError ||
    common.hasPhotoOrderLimitError ||
    isStartDateInvalid ||
    common.isStartDateMissing
  );
};

export const isInvalidEasyOrderData = (params) => {
  if (getIsEditMode(params.formData)) {
    return isInvalidEasyOrderDataForEdit(params);
  }
  return isInvalidEasyOrderDataForCreation(params);
};

// ─── Points on Order Delivery ───

export const isInvalidPointsOrderDeliverForEdit = ({
  formData,
  dateFns,
  isCountryCodeFrequency,
  validateStartDate = true
}) => {
  const {
    startDate,
    endDate,
    pointsEarned,
    pointsEarnedValid,
    startDateValid,
    endDateValid,
    numberOfCases,
    numberOfCasesValid,
    localCurrency,
    localCurrencyValid,
    activityName,
    orderType
  } = formData;

  const isStartDateMissing = !isNotEmptyAndDefined(startDate);
  const validStartDate = validateStartDate
    ? checkDateValid(startDate, dateFns.validateStartDate)
    : true;
  const isEndDateMissing = !isNotEmptyAndDefined(endDate);
  const isCurrencyFrequencyValid = isCountryCodeFrequency
    ? numberOfCasesValid === true || !isNotEmptyAndDefined(numberOfCases)
    : localCurrencyValid === true || !isNotEmptyAndDefined(localCurrency);

  const hasPointsEarnedError = pointsEarnedValid === true;
  const hasStartDateError = startDateValid === true;
  const hasEndDateError = endDateValid === true;

  return (
    hasPointsEarnedError ||
    !validStartDate ||
    isCurrencyFrequencyValid ||
    !isNotEmptyAndDefined(pointsEarned) ||
    !isNotEmptyAndDefined(activityName) ||
    hasStartDateError ||
    hasEndDateError ||
    isStartDateMissing ||
    isEndDateMissing ||
    !isNotEmptyAndDefined(orderType)
  );
};

export const isInvalidPointsOrderDeliverForCreation = ({
  formData,
  isCountryCodeFrequency,
  validateStartDate = true
}) => {
  const {
    endDate,
    pointsEarned,
    pointsEarnedValid,
    startDateValid,
    numberOfCases,
    numberOfCasesValid,
    localCurrency,
    localCurrencyValid,
    activityName,
    orderType
  } = formData;

  const isStartDateInvalid = validateStartDate
    ? validateDate(startDateValid)
    : true;
  const isEndDateInvalid = !isStartDateInvalid && endDate === undefined;
  const isCurrencyFrequencyValid = isCountryCodeFrequency
    ? numberOfCasesValid || !isNotEmptyAndDefined(numberOfCases)
    : localCurrencyValid || !isNotEmptyAndDefined(localCurrency);
  const hasPointsEarnedError = pointsEarnedValid === true;
  return (
    hasPointsEarnedError ||
    isCurrencyFrequencyValid ||
    !isNotEmptyAndDefined(pointsEarned) ||
    !isNotEmptyAndDefined(activityName) ||
    isStartDateInvalid ||
    isEndDateInvalid ||
    !isNotEmptyAndDefined(orderType)
  );
};

export const isInvalidPointsOrderDeliver = (params) => {
  if (getIsEditMode(params.formData)) {
    return isInvalidPointsOrderDeliverForEdit(params);
  }
  return isInvalidPointsOrderDeliverForCreation(params);
};

// ─── Point Expiration ───

export const isInvalidPointExpirationData = ({formData}) => {
  const isNoOfDaysInvalid = !isNotEmptyAndDefined(formData?.noOfDays);

  const noOfDaysValue = parseInt(formData?.noOfDays, 10);
  const isNoOfDaysOutOfRange =
    isNaN(noOfDaysValue) || noOfDaysValue < 1 || noOfDaysValue > 365;

  return isNoOfDaysInvalid || isNoOfDaysOutOfRange;
};

// ─── Pay by Points ───

export const isInvalidPayByPointsDataForEdit = ({
  formData,
  dateFns,
  validateStartDate = true
}) => {
  const {
    startDate,
    endDate,
    hasEndDate,
    pointsToRedeem,
    pointsToRedeemValid,
    localCurrencyValue,
    localCurrencyValueValid,
    startDateValid,
    endDateValid,
    activityName
  } = formData;

  const isStartDateMissing = !isNotEmptyAndDefined(startDate);
  const validStartDate = validateStartDate
    ? checkDateValid(startDate, dateFns.validateStartDate)
    : true;
  const isEndDateMissingWhenRequired =
    hasEndDate && !isNotEmptyAndDefined(endDate);
  const isEndDateInvalidWhenRequired =
    hasEndDate && endDate && !checkDateValid(endDate, dateFns.validateEndDate);
  const hasPointsToRedeemError = pointsToRedeemValid === true;
  const hasLocalCurrencyValueError = localCurrencyValueValid === true;
  const hasStartDateError = startDateValid === true;
  const hasEndDateError = hasEndDate && endDateValid === true;
  return (
    hasPointsToRedeemError ||
    hasLocalCurrencyValueError ||
    !validStartDate ||
    !isNotEmptyAndDefined(pointsToRedeem) ||
    !isNotEmptyAndDefined(localCurrencyValue) ||
    !isNotEmptyAndDefined(activityName) ||
    hasStartDateError ||
    hasEndDateError ||
    isStartDateMissing ||
    isEndDateMissingWhenRequired ||
    isEndDateInvalidWhenRequired
  );
};

export const isInvalidPayByPointsDataForCreation = ({
  formData,
  validateStartDate = true
}) => {
  const {
    endDate,
    hasEndDate,
    pointsToRedeem,
    pointsToRedeemValid,
    localCurrencyValue,
    localCurrencyValueValid,
    startDateValid,
    endDateValid,
    activityName
  } = formData;

  const isStartDateInvalid = validateStartDate
    ? validateDate(startDateValid)
    : true;
  const isEndDateInvalidWhenRequired =
    hasEndDate &&
    (!isNotEmptyAndDefined(endDate) || validateDate(endDateValid));
  const hasPointsToRedeemError = pointsToRedeemValid === true;
  const hasLocalCurrencyValueError = localCurrencyValueValid === true;
  const isPointsToRedeemInvalid = !isNotEmptyAndDefined(pointsToRedeem);
  const isLocalCurrencyValueInvalid = !isNotEmptyAndDefined(localCurrencyValue);
  const isActivityNameInvalid = !isNotEmptyAndDefined(activityName);
  return (
    hasPointsToRedeemError ||
    hasLocalCurrencyValueError ||
    isPointsToRedeemInvalid ||
    isLocalCurrencyValueInvalid ||
    isActivityNameInvalid ||
    isStartDateInvalid ||
    isEndDateInvalidWhenRequired
  );
};

export const isInvalidPayByPointsData = (params) => {
  if (getIsEditMode(params.formData)) {
    return isInvalidPayByPointsDataForEdit(params);
  }
  return isInvalidPayByPointsDataForCreation(params);
};

const getCurrencyToPointsCommonChecks = (formData) => {
  const {
    startDate,
    endDate,
    pointsEarned,
    localCurrency,
    pointsEarnedValid,
    hasEndDate
  } = formData;

  const isStartDateMissing = !isNotEmptyAndDefined(startDate);
  const isPointsEarnedInvalid = !isNotEmptyAndDefined(pointsEarned);
  const isLocalCurrencyInvalid = !isNotEmptyAndDefined(localCurrency);
  const hasPointsError = pointsEarnedValid === true || isPointsEarnedInvalid;

  if (hasEndDate) {
    const isEndDateMissing = !isNotEmptyAndDefined(endDate);
    return {
      isStartDateMissing,
      isPointsEarnedInvalid,
      isLocalCurrencyInvalid,
      hasPointsError,
      isEndDateMissing,
      hasEndDate
    };
  }
  return {
    isStartDateMissing,
    isPointsEarnedInvalid,
    isLocalCurrencyInvalid,
    hasPointsError,
    hasEndDate
  };
};

// ─── Currency to Points ───

export const isInvalidCurrencyToPointsDataForEdit = ({
  formData,
  dateFns,
  validateStartDate = true
}) => {
  const common = getCurrencyToPointsCommonChecks(formData);
  const validStartDate = validateStartDate
    ? checkDateValid(formData.startDate, dateFns.validateStartDate)
    : true;
  const hasStartDateError = formData.startDateValid === true;
  const hasEndDateError = formData.endDateValid === true;

  if (common.hasEndDate) {
    return (
      !validStartDate ||
      common.hasPointsError ||
      hasStartDateError ||
      hasEndDateError ||
      common.isStartDateMissing ||
      common.isEndDateMissing ||
      common.isLocalCurrencyInvalid
    );
  }
  return (
    !validStartDate ||
    common.hasPointsError ||
    hasStartDateError ||
    common.isStartDateMissing ||
    common.isLocalCurrencyInvalid
  );
};

export const isInvalidCurrencyToPointsDataForCreation = ({
  formData,
  validateStartDate = true
}) => {
  const common = getCurrencyToPointsCommonChecks(formData);
  const isStartDateInvalid = validateStartDate
    ? validateDate(formData.startDateValid)
    : true;

  if (common.hasEndDate) {
    const isEndDateInvalid = validateDate(formData.endDateValid);
    return (
      isStartDateInvalid ||
      isEndDateInvalid ||
      common.isStartDateMissing ||
      common.isEndDateMissing ||
      common.hasPointsError ||
      common.isLocalCurrencyInvalid
    );
  }
  return (
    isStartDateInvalid ||
    common.isStartDateMissing ||
    common.hasPointsError ||
    common.isLocalCurrencyInvalid
  );
};

export const isInvalidCurrencyToPointsData = (params) => {
  if (getIsEditMode(params.formData)) {
    return isInvalidCurrencyToPointsDataForEdit(params);
  }
  return isInvalidCurrencyToPointsDataForCreation(params);
};

const getCommonActivityFieldChecks = (formData) => {
  const {activityName, startDate, endDate, startDateValid, endDateValid} =
    formData;
  return {
    nameInvalid:
      !isNotEmptyAndDefined(activityName) || String(activityName).trim() === '',
    startMissing: !isNotEmptyAndDefined(startDate),
    endMissing: !isNotEmptyAndDefined(endDate),
    hasStartFieldError: startDateValid === true,
    hasEndFieldError: endDateValid === true
  };
};

// ─── Video ───

export const isInvalidVideoData = ({
  formData,
  dateFns,
  validateStartDate = true
}) => {
  const {pointsEarned, pointsEarnedValid, allTimeLimit, allTimeLimitValid} =
    formData;
  const common = getCommonActivityFieldChecks(formData);
  const pointsInvalid =
    !isNotEmptyAndDefined(pointsEarned) || pointsEarnedValid === true;
  const validVideoStart = validateStartDate
    ? checkDateValid(formData.startDate, dateFns.validateVideoStartDate)
    : true;
  const validVideoEnd = checkDateValid(
    formData.endDate,
    dateFns.validateVideoEndDate
  );
  const hasAllTimeError = allTimeLimitValid === true;
  const allTimeEmpty = !isNotEmptyAndDefined(allTimeLimit);
  const allTimeNum = Number(String(allTimeLimit ?? '').trim());
  const allTimeNotPositive =
    !allTimeEmpty &&
    (!Number.isFinite(allTimeNum) ||
      allTimeNum <= 0 ||
      !Number.isInteger(allTimeNum));

  if (getIsEditMode(formData)) {
    return (
      common.nameInvalid ||
      pointsInvalid ||
      common.startMissing ||
      common.endMissing ||
      !validVideoStart ||
      !validVideoEnd ||
      common.hasStartFieldError ||
      common.hasEndFieldError ||
      allTimeEmpty ||
      hasAllTimeError ||
      allTimeNotPositive
    );
  }

  return (
    common.nameInvalid ||
    pointsInvalid ||
    common.startMissing ||
    common.endMissing ||
    !validVideoStart ||
    !validVideoEnd ||
    validateDate(formData.startDateValid) ||
    validateDate(formData.endDateValid) ||
    allTimeEmpty ||
    hasAllTimeError ||
    allTimeNotPositive
  );
};

// ─── Survey ───

export const isInvalidSurveyData = ({
  formData,
  dateFns,
  validateStartDate = true
}) => {
  const {pointsEarned, pointsEarnedValid, allTimeLimit, allTimeLimitValid} =
    formData;
  const common = getCommonActivityFieldChecks(formData);
  const pointsInvalid =
    !isNotEmptyAndDefined(pointsEarned) || pointsEarnedValid === true;
  const validSurveyStart = validateStartDate
    ? checkDateValid(formData.startDate, dateFns.validateSurveyStartDate)
    : true;
  const validSurveyEnd = checkDateValid(
    formData.endDate,
    dateFns.validateSurveyEndDate
  );
  const hasAllTimeError = allTimeLimitValid === true;
  const allTimeEmpty = !isNotEmptyAndDefined(allTimeLimit);
  const allTimeNum = Number(String(allTimeLimit ?? '').trim());
  const allTimeNotPositive =
    !allTimeEmpty &&
    (!Number.isFinite(allTimeNum) ||
      allTimeNum <= 0 ||
      !Number.isInteger(allTimeNum));
  const displayOrderInvalid = isSurveyDisplayOrderInvalid(
    formData.displayOrder
  );

  if (getIsEditMode(formData)) {
    return (
      common.nameInvalid ||
      pointsInvalid ||
      common.startMissing ||
      common.endMissing ||
      !validSurveyStart ||
      !validSurveyEnd ||
      common.hasStartFieldError ||
      common.hasEndFieldError ||
      allTimeEmpty ||
      hasAllTimeError ||
      allTimeNotPositive ||
      displayOrderInvalid
    );
  }

  return (
    common.nameInvalid ||
    pointsInvalid ||
    common.startMissing ||
    common.endMissing ||
    !validSurveyStart ||
    !validSurveyEnd ||
    validateDate(formData.startDateValid) ||
    validateDate(formData.endDateValid) ||
    allTimeEmpty ||
    hasAllTimeError ||
    allTimeNotPositive ||
    displayOrderInvalid
  );
};

// ─── Game ───

export const isInvalidGameData = ({formData}) => {
  const common = getCommonActivityFieldChecks(formData);
  const {dailyDisplayStartTime, dailyDisplayEndTime} = formData;

  const hasStartTime = isNotEmptyAndDefined(dailyDisplayStartTime);
  const hasEndTime = isNotEmptyAndDefined(dailyDisplayEndTime);
  const isTimeFieldsIncomplete = hasStartTime !== hasEndTime;

  const isDailyPlayLimitInvalid = validateDailyPlayLimit(
    formData?.dailyPlayLimit
  );

  return (
    common.nameInvalid ||
    common.startMissing ||
    common.endMissing ||
    common.hasStartFieldError ||
    common.hasEndFieldError ||
    isDailyPlayLimitInvalid ||
    isTimeFieldsIncomplete
  );
};

// ─── Wheel of Fortune ───

export const isInvalidWheelOfFortuneData = ({
  formData,
  dateFns,
  validateStartDate = true
}) => {
  const {
    pointsEarned,
    pointsEarnedValid,
    budgetPeriod,
    segmentExclude,
    excludedSegments,
    dailyDisplayStartTime,
    dailyDisplayEndTime
  } = formData;

  const common = getCommonActivityFieldChecks(formData);
  const budgetInvalid =
    !isNotEmptyAndDefined(pointsEarned) || pointsEarnedValid === true;
  const budgetPeriodInvalid = !isNotEmptyAndDefined(budgetPeriod);
  const validWofStart = validateStartDate
    ? checkDateValid(formData.startDate, dateFns.validateStartDate)
    : true;
  const validWofEnd = checkDateValid(formData.endDate, dateFns.validateEndDate);
  const noSegmentsSelected = getNoSegmentsSelected(formData);
  const noExcludedSegmentsSelected =
    segmentExclude === true &&
    (!Array.isArray(excludedSegments) || excludedSegments.length === 0);

  const isDailyPlayLimitInvalid = validateDailyPlayLimit(
    formData?.dailyPlayLimit
  );

  const hasStartTime = isNotEmptyAndDefined(dailyDisplayStartTime);
  const hasEndTime = isNotEmptyAndDefined(dailyDisplayEndTime);
  const isTimeFieldsIncomplete = hasStartTime !== hasEndTime;

  if (getIsEditMode(formData)) {
    return (
      common.nameInvalid ||
      budgetInvalid ||
      budgetPeriodInvalid ||
      common.startMissing ||
      common.endMissing ||
      !validWofStart ||
      !validWofEnd ||
      common.hasStartFieldError ||
      common.hasEndFieldError ||
      noSegmentsSelected ||
      noExcludedSegmentsSelected ||
      isDailyPlayLimitInvalid ||
      isTimeFieldsIncomplete
    );
  }

  return (
    common.nameInvalid ||
    budgetInvalid ||
    budgetPeriodInvalid ||
    common.startMissing ||
    common.endMissing ||
    !validWofStart ||
    !validWofEnd ||
    validateDate(formData.startDateValid) ||
    validateDate(formData.endDateValid) ||
    noSegmentsSelected ||
    noExcludedSegmentsSelected ||
    isDailyPlayLimitInvalid ||
    isTimeFieldsIncomplete
  );
};

// ─── Master validator ───

export const validateFormData = ({
  formData,
  dateFns,
  isCountryCodeFrequency,
  validateStartDate = true
}) => {
  if (getNoSegmentsSelected(formData)) {
    return true;
  }

  const activityType = formData?.activityType;

  const baseParams = {formData, dateFns, validateStartDate};

  const activityValidators = {
    [ACTIVITY_TYPE.LOGIN]: () => isInvalidLoginData(baseParams),
    [ACTIVITY_TYPES_ENUM.LOGIN]: () => isInvalidLoginData(baseParams),
    [ACTIVITY_TYPE.WELCOME_ACTIVITY]: () => isInvalidWelcomeData(baseParams),
    [ACTIVITY_TYPES_ENUM.WELCOME]: () => isInvalidWelcomeData(baseParams),
    [ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY]: () =>
      isInvalidPointsOrderDeliver({...baseParams, isCountryCodeFrequency}),
    [ACTIVITY_TYPES_ENUM.POINTS_ON_ORDER_DELIVERY]: () =>
      isInvalidPointsOrderDeliver({...baseParams, isCountryCodeFrequency}),
    [ACTIVITY_TYPE.POINT_EXPIRATION]: () =>
      isInvalidPointExpirationData({formData}),
    [ACTIVITY_TYPES_ENUM.POINT_EXPIRATION]: () =>
      isInvalidPointExpirationData({formData}),
    [ACTIVITY_TYPE.PAY_BY_POINTS]: () => isInvalidPayByPointsData(baseParams),
    [ACTIVITY_TYPES_ENUM.PAY_BY_POINTS]: () =>
      isInvalidPayByPointsData(baseParams),
    [ACTIVITY_TYPE.SURVEY]: () => isInvalidSurveyData(baseParams),
    [ACTIVITY_TYPES_ENUM.SURVEY]: () => isInvalidSurveyData(baseParams),
    [ACTIVITY_TYPE.GAME_WHEEL_OF_FORTUNE]: () =>
      isInvalidWheelOfFortuneData(baseParams),
    [ACTIVITY_TYPES_ENUM.GAME_WHEEL_OF_FORTUNE]: () =>
      isInvalidWheelOfFortuneData(baseParams),
    [ACTIVITY_TYPE.CURRENCY_TO_POINTS]: () =>
      isInvalidCurrencyToPointsData(baseParams),
    [ACTIVITY_TYPES_ENUM.CURRENCY_TO_POINTS]: () =>
      isInvalidCurrencyToPointsData(baseParams),
    [ACTIVITY_TYPE.EASY_ORDER]: () => isInvalidEasyOrderData(baseParams),
    [ACTIVITY_TYPES_ENUM.EASY_ORDER]: () => isInvalidEasyOrderData(baseParams)
  };

  const validator = activityValidators[activityType];
  if (validator) {
    return validator();
  }

  if (isVideoActivityType(activityType)) {
    return isInvalidVideoData(baseParams);
  }
  if (isFreeKickActivityType(activityType)) {
    return isInvalidGameData({formData});
  }
  if (isBackgammonActivityType(activityType)) {
    return isInvalidGameData({formData});
  }

  return !activityType;
};
